import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";

import useTypedContext from "hooks/useTypedContext";
import useUpdateStack from "shared/Stack/useUpdateStack";
import { VcsProvider } from "types/generated";
import FormSourceCode from "components/Forms/SourceCode";
import { SourceCodeProjects } from "components/Forms/SourceCode/types";
import FormFieldProjectGlobs from "components/FormFields/ProjectGlobs";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import { InternalFormData } from "components/Forms/SourceCode/types";
import Card from "ds/components/Card";

import { StackSettingsContextData } from "../Context";
import {
  getFormDefaultValues,
  getProviderEmptyValues,
  mapCreateStackSourceCodeUpdateInput,
} from "./helpers";
import StackSettingsFormFooter from "../components/FormFooter";
import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../constants";

export type StackSettingsSourceCodeFormFields = {
  namespace: string;
  repository: string;
  projectRoot: string;
  branch: string;
  provider: VcsProvider;
  vcsIntegrationId: string | undefined;
  repositoryURL?: string;
  additionalProjectGlobs: Array<{ value: string }>;
  gitSparseCheckoutPaths: Array<{ value: string }>;
  useGitCheckout?: boolean;
};

const StackSettingsSourceCodeEdit = () => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const [enableSparseCheckout, setEnableSparseCheckout] = useState<InternalFormData>({
    useGitCheckout: stackSettings.vcsIntegration?.details?.useGitCheckout,
  });

  const { stackUpdate, loading } = useUpdateStack({
    stack: stackSettings,
    refetchQueries: REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
  });

  const sourceCodeForm = useForm<StackSettingsSourceCodeFormFields>({
    defaultValues: getFormDefaultValues(stackSettings),
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
    trigger,
  } = sourceCodeForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const resetFormFields = (type: VcsProvider) => {
    if (type === stackSettings.provider) {
      reset(getFormDefaultValues(stackSettings));
    } else {
      reset({
        ...getFormDefaultValues(stackSettings),
        ...getProviderEmptyValues(),
        provider: type,
      });
    }

    trigger();
  };

  const onSubmit = (formData: StackSettingsSourceCodeFormFields) => {
    stackUpdate(mapCreateStackSourceCodeUpdateInput(formData));
  };

  const values = watch();

  useEffect(() => {
    reset(getFormDefaultValues(stackSettings));
  }, [reset, stackSettings]);

  return (
    <FormProvider {...sourceCodeForm}>
      <FormSourceCode
        spaceId={stackSettings.spaceDetails.id}
        provider={values.provider}
        vcsIntegrationId={values.vcsIntegrationId}
        repository={values.repository}
        resetFormFields={resetFormFields}
        projectType={SourceCodeProjects.Stack}
        innerFormMargin="0"
        innerFormGap="x-large"
        useGitCheckout={enableSparseCheckout.useGitCheckout}
        setInternalFormData={setEnableSparseCheckout}
      />

      <Card variant="secondary" contentGap="large">
        <FormFieldProjectGlobs />
      </Card>

      <StackSettingsFormFooter
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(stackSettings))}
        isDirty={isDirty}
        isSubmitDisabled={loading}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default StackSettingsSourceCodeEdit;
